import type { ReactPageFunction } from '../../../js/cloudflare/types';
import { productAPI } from '../../../js/config';
import GiftcardPage from '../../../js/giftcard/GiftCardPage';
import HeaderFooterLayout from '../../../js/layout/HeaderFooterLayout';
import type { StaticLayoutProps } from '../../../js/layout/Layout';
import { isGiftCard } from '../../../js/model/Product';
import type { Availability } from '../../../js/product/ProductAvailability';
import type { DetailedProduct } from '../../../js/product/productdetails/ProductDetailsPage';
import ProductDetailsPage from '../../../js/product/productdetails/ProductDetailsPage';
import { createMetaDescription } from '../../../js/product/productdetails/structuredProductData';
import { setCacheControl } from '../../../js/utils/responseHelpers';

interface Props {
    product: DetailedProduct;
}

export const onRequestGet: ReactPageFunction<'[[slug]]/p/[id]', Props> = async ({ request, params, next, renderToResponse, data }) => {
    const url = new URL(request.url);
    const productId = params.id;

    const response = await data.fetchFromBackend(`${productAPI.productURL(productId)}?${new URLSearchParams({ fields: 'FULL' })}`);

    if (response.status === 404) return next();

    if (response.status !== 200) throw new Error(`Request to ${productAPI.productURL(productId)} responded with ${response.status}`);

    const product = await response.json<DetailedProduct>().catch(() => undefined);

    if (!product) return next();

    if (url.pathname !== product.url) {
        return Response.redirect(new URL(product.url, url), 301);
    } else {
        // TODO: this should be done in the backend eventually
        const availability = await data
            .fetchFromBackend(productAPI.userAvailability(product.code))
            .then((r) => r.json<Availability | undefined>())
            .catch(() => undefined);
        product.productAvailability = availability;

        return renderToResponse({
            product,
        }).then(setCacheControl(`public, max-age=${5 * 60}, stale-while-revalidate=${60 * 60 * 24}`));
    }
};

export default function ({ product }: Props) {
    switch (product.code) {
        case 'gavekort':
            return <GiftcardPage productId={product.code} gavekort={product} />;
        case 'egavekort':
            return <GiftcardPage productId={product.code} egavekort={product} />;
        default:
            return <ProductDetailsPage product={product} />;
    }
}

export function StaticLayout({ props: { product }, url }: StaticLayoutProps<Props>) {
    const description = createMetaDescription(product);
    const canonicalUrl = new URL(product.url, url).toString();
    return (
        <HeaderFooterLayout
            title={product.name}
            page_title={isGiftCard(product) ? 'Gavekort' : 'Produkt'}
            canonical={canonicalUrl}
            // HACKY SOLUTION TO A PROBLEM
            robots={product.code === '13384801' ? 'noindex,nofollow' : undefined}
            meta={[
                { name: 'description', content: description },
                { property: 'og:locale', content: 'nb_NO' },
                { property: 'og:type', content: 'website' },
                { property: 'og:site_name', content: 'vinmonopolet.no' },
                { property: 'twitter:card', content: 'summary_large_image' },
                { property: 'og:description', content: description },
                { property: 'og:title', content: product.name },
                { property: 'og:url', content: canonicalUrl },
                { property: 'og:image', content: product.images?.find((i) => i.format == 'superZoom')?.url },
            ]}
        />
    );
}
